/**
 * Chitravan platform API client — used by V2 produce + studio pages.
 * Mirrors V1's authHeader + fetch pattern (Bearer JWT obtained from
 * /api/auth/token in the parent web app, propagated here via cookie auth).
 */

export const API_BASE = process.env.NEXT_PUBLIC_CHITRAVAN_API ?? "/api";

// Token endpoint lives on the PARENT web app (chitravan.co.in/api/auth/token,
// not /v2/api/auth/token — the V2 stub at that path returns 410). We use an
// absolute origin path that is NOT prefixed by Next's basePath="/v2".
//
// Implementation note: `fetch("/api/auth/token")` from a V2 client component
// is rewritten to `/v2/api/auth/token` because of basePath. Building the URL
// from window.location.origin sidesteps that rewrite and hits the real auth
// endpoint on the parent web app. SSR has no window — fall through to cookie
// auth in that case (the API also accepts session cookies).
async function tokenUrl(): Promise<string> {
	if (typeof window === "undefined") return "/api/auth/token";
	return `${window.location.origin}/api/auth/token`;
}

// Auth-token cache. The token itself is a short-lived JWT; the upstream
// /api/auth/token endpoint takes 0.4-1.1 s per round-trip on cold load and
// was being hit on EVERY api() call (measured: 2-3× per studio load = wasted
// ~2 s). Cache in-memory for 4 minutes (below the JWT's typical 5-min TTL so
// we always have headroom). Mutations and refreshes go through the normal
// invalidate() path. SSR has no cache (returns fresh every time).
type TokenEntry = { value: string; expiresAt: number };
const TOKEN_CACHE_TTL_MS = 4 * 60 * 1000;
let tokenCache: TokenEntry | null = null;
let tokenInflight: Promise<string> | null = null;

async function fetchTokenOnce(): Promise<string> {
	const url = await tokenUrl();
	const res = await fetch(url, { credentials: "include" });
	if (!res.ok) return "";
	const d = await res.json();
	return d.token || "";
}

export function invalidateAuthCache(): void {
	tokenCache = null;
	tokenInflight = null;
}

export async function authHeader(): Promise<Record<string, string>> {
	try {
		if (typeof window === "undefined") {
			const t = await fetchTokenOnce();
			return t ? { Authorization: `Bearer ${t}` } : {};
		}
		const now = Date.now();
		if (tokenCache && tokenCache.expiresAt > now) {
			return { Authorization: `Bearer ${tokenCache.value}` };
		}
		// Coalesce concurrent token fetches. Without this, if 5 components
		// fan out api() calls in the same tick on cold load, we fire 5
		// parallel /api/auth/token requests — exactly what we're trying to
		// avoid. Sharing one in-flight promise means N callers = 1 fetch.
		if (!tokenInflight) {
			tokenInflight = fetchTokenOnce().finally(() => {
				tokenInflight = null;
			});
		}
		const t = await tokenInflight;
		if (t) tokenCache = { value: t, expiresAt: now + TOKEN_CACHE_TTL_MS };
		return t ? { Authorization: `Bearer ${t}` } : {};
	} catch {
		return {};
	}
}

function absUrl(path: string): string {
	// Same basePath bypass as tokenUrl: build an absolute origin URL so
	// `fetch("/api/...")` doesn't get rewritten to `/v2/api/...`.
	if (typeof window === "undefined") return `${API_BASE}${path}`;
	return `${window.location.origin}${API_BASE}${path}`;
}

// In-flight GET dedup. When several components mount in the same tick and
// each calls api("/episodes/{id}"), we get 3+ identical network requests
// (measured: episode endpoint hit 3× per studio load, 147 KB each = ~440 KB
// wasted). Coalescing concurrent callers to a single promise eliminates the
// duplication without changing any caller code. Only safe for read methods
// (GET/HEAD) — mutations always go through a fresh request even when the
// URL matches, because each could have different intent or body.
const inflightGets = new Map<string, Promise<unknown>>();

function getMethod(init?: RequestInit): string {
	return (init?.method ?? "GET").toUpperCase();
}

// ====================================================================
// localStorage stale-while-revalidate cache (Stage 3 of the perf roadmap).
//
// Why: cold load makes 24-27 API calls (~3.4 MB). On warm re-entry the
// same calls fire again — bundle is cached but data isn't. Returning
// cached responses immediately and revalidating in the background drops
// time-to-interactive from ~13 s to ~1-2 s on the warm path.
//
// What: each GET response is stored under "chitravan:api:<path>" with a
// timestamp. On read:
//   * fresh (< TTL/2)        → return cached, no refresh
//   * stale-ish (TTL/2..TTL) → return cached, refresh in background
//   * expired (≥ TTL)        → cache miss, fetch fresh
//
// Mutations (POST/PUT/DELETE) clear the cache for the same path so the
// next read pulls fresh data. Caller can also call invalidateApiCache().
//
// Excludes /auth/token (has its own in-memory cache above) and
// /credits/balance (always fresh — money UX must never lie).
// ====================================================================

const CACHE_PREFIX = "chitravan:api:";
const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 min
const NO_CACHE_PATTERNS = ["/auth/token", "/credits/balance"];

// ── Per-user cache ownership (SECURITY) ──────────────────────────────
// The api() localStorage cache is shared per-BROWSER. Without an owner check,
// logging into account B in the same browser would serve account A's cached
// GET /projects (etc.) until the 5-min TTL expired — a cross-account data leak.
// Every cache entry is stamped with the user id it belongs to, and readCache
// ONLY returns an entry whose owner === the current session user. A mismatch
// (or unknown owner) is a cache MISS → fetch fresh. Worst case is a redundant
// fetch; it can never serve one user's data to another.
const CACHE_OWNER_KEY = "chitravan:cache-owner";

function currentOwnerId(): string | null {
	if (typeof window === "undefined") return null;
	try {
		return window.localStorage.getItem(CACHE_OWNER_KEY);
	} catch {
		return null;
	}
}

/** Record the logged-in user; if it changed, purge all cached responses + token. */
export function setCacheOwner(userId: string | null): void {
	if (typeof window === "undefined") return;
	try {
		const prev = window.localStorage.getItem(CACHE_OWNER_KEY);
		const next = userId ?? "";
		if (prev !== next) {
			invalidateApiCache(); // drop every chitravan:api:* entry from the old owner
			invalidateAuthCache(); // in-memory JWT belonged to the old owner
			if (next) window.localStorage.setItem(CACHE_OWNER_KEY, next);
			else window.localStorage.removeItem(CACHE_OWNER_KEY);
		}
	} catch {/* localStorage unavailable — cache is simply off, no leak */}
}

function isCacheable(method: string, path: string): boolean {
	if (method !== "GET" && method !== "HEAD") return false;
	if (typeof window === "undefined") return false;
	return !NO_CACHE_PATTERNS.some((p) => path.includes(p));
}

interface CacheEntry<T> {
	value: T;
	storedAt: number;
	owner?: string | null; // user id this response belongs to (SECURITY)
}

function readCache<T>(path: string): CacheEntry<T> | null {
	try {
		const raw = window.localStorage.getItem(CACHE_PREFIX + path);
		if (!raw) return null;
		const entry = JSON.parse(raw) as CacheEntry<T>;
		// SECURITY: never serve one user's cached response to another. An entry
		// with no owner, or a different owner than the current session, is a miss.
		const owner = currentOwnerId();
		if ((entry.owner ?? null) !== (owner ?? null)) return null;
		return entry;
	} catch {
		return null;
	}
}

function writeCache<T>(path: string, value: T): void {
	try {
		const payload = JSON.stringify({ value, storedAt: Date.now(), owner: currentOwnerId() });
		window.localStorage.setItem(CACHE_PREFIX + path, payload);
	} catch (e) {
		// localStorage quota is ~5-10 MB. If we overflow, drop oldest half
		// of api: entries and retry once. Beyond that, silently degrade —
		// we just skip caching this entry, the app still works.
		if (e instanceof DOMException && e.name === "QuotaExceededError") {
			pruneOldestCacheEntries();
			try {
				window.localStorage.setItem(
					CACHE_PREFIX + path,
					JSON.stringify({ value, storedAt: Date.now() }),
				);
			} catch {/* give up */}
		}
	}
}

function pruneOldestCacheEntries(): void {
	const entries: Array<[string, number]> = [];
	for (let i = 0; i < window.localStorage.length; i++) {
		const k = window.localStorage.key(i);
		if (k?.startsWith(CACHE_PREFIX)) {
			try {
				const v = JSON.parse(window.localStorage.getItem(k) || "{}");
				entries.push([k, typeof v.storedAt === "number" ? v.storedAt : 0]);
			} catch {/* skip malformed */}
		}
	}
	entries.sort((a, b) => a[1] - b[1]);
	const dropCount = Math.max(1, Math.floor(entries.length / 2));
	for (let i = 0; i < dropCount; i++) {
		window.localStorage.removeItem(entries[i][0]);
	}
}

/**
 * Clear cached responses. Call after a mutation in a different component
 * than the one that made the change, or to force-refresh.
 *
 * - no arg: clear ALL api cache
 * - string: clear entries whose path starts with this prefix
 */
export function invalidateApiCache(pathPrefix?: string): void {
	if (typeof window === "undefined") return;
	const fullPrefix = pathPrefix ? CACHE_PREFIX + pathPrefix : CACHE_PREFIX;
	const toRemove: string[] = [];
	for (let i = 0; i < window.localStorage.length; i++) {
		const k = window.localStorage.key(i);
		if (k?.startsWith(fullPrefix)) toRemove.push(k);
	}
	toRemove.forEach((k) => window.localStorage.removeItem(k));
}

async function doFetch<T>(path: string, init?: RequestInit): Promise<T> {
	const h = await authHeader();
	const res = await fetch(absUrl(path), {
		...init,
		credentials: "include",
		headers: {
			"Content-Type": "application/json",
			...h,
			...(init?.headers ?? {}),
		},
	});
	return await parseResponse<T>(res);
}

export async function api<T = unknown>(
	path: string,
	init?: RequestInit
): Promise<T> {
	const method = getMethod(init);
	const isRead = method === "GET" || method === "HEAD";
	// `cache: "no-store"` means "I need the live answer": skip the localStorage
	// cache and in-flight sharing. Used by polls and by reloads after a change.
	const noCache = init?.cache === "no-store";
	const cacheable = !noCache && isCacheable(method, path);

	// Stale-while-revalidate fast path: return cached if fresh, refresh in
	// background if stale-ish.
	if (cacheable) {
		const entry = readCache<T>(path);
		if (entry) {
			const age = Date.now() - entry.storedAt;
			if (age < DEFAULT_TTL_MS) {
				if (age > DEFAULT_TTL_MS / 2) {
					// Background revalidate. Fire-and-forget. On success, write
					// fresh value + dispatch event so subscribers can re-render.
					void doFetch<T>(path, init)
						.then((fresh) => {
							writeCache(path, fresh);
							window.dispatchEvent(
								new CustomEvent("chitravan:api-updated", {
									detail: { path },
								}),
							);
						})
						.catch(() => {/* keep stale, network down or rate-limited */});
				}
				return entry.value;
			}
		}
	}

	// Cache miss / non-cacheable: existing in-flight dedup, then real fetch.
	if (isRead && !noCache) {
		const inflight = inflightGets.get(path);
		if (inflight) return inflight as Promise<T>;
	}

	const fetchPromise = doFetch<T>(path, init);
	if (isRead && !noCache) {
		inflightGets.set(path, fetchPromise);
		void fetchPromise.finally(() => inflightGets.delete(path));
	}

	try {
		const result = await fetchPromise;
		// A live read also refreshes the cache so later cached reads are current.
		if (isCacheable(method, path)) writeCache(path, result);
		return result;
	} finally {
		if (!isRead && typeof window !== "undefined") {
			// Mutation completed (success OR failure) — drop cache for the
			// same path so the next GET pulls fresh state. We don't try to
			// be clever about related paths; callers can call
			// invalidateApiCache("<prefix>") for cross-cutting invalidations.
			try {
				window.localStorage.removeItem(CACHE_PREFIX + path);
			} catch {/* no-op */}
			window.dispatchEvent(
				new CustomEvent("chitravan:api-invalidate", { detail: { path } }),
			);
			notifyCreditsMayHaveChanged(path);
		}
	}
}

// Anything that can charge or refund credits tells the credit pill to read the balance again (a failed call can refund, so success or failure both count).
// Bursts (several pictures or clips started together) become one read.
const CREDIT_PATHS = ["/generate/", "/scene-planner/", "/ideaboard/", "/platform/", "/credits"];
let creditsTimer: ReturnType<typeof setTimeout> | null = null;
function notifyCreditsMayHaveChanged(path: string): void {
	if (!CREDIT_PATHS.some((p) => path.startsWith(p))) return;
	if (creditsTimer) clearTimeout(creditsTimer);
	creditsTimer = setTimeout(() => {
		creditsTimer = null;
		window.dispatchEvent(new CustomEvent("chitravan:credits-changed"));
	}, 400);
}

async function parseResponse<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown;
	try {
		data = text ? JSON.parse(text) : {};
	} catch {
		data = { raw: text };
	}
	if (!res.ok) {
		// FastAPI's `detail` is sometimes a string (custom HTTPException),
		// sometimes an array of {loc, msg, type} dicts (422 validation),
		// sometimes a dict. Coerce to a readable string before throwing —
		// otherwise React renders "[object Object],[object Object]" in the
		// caller's error UI.
		const raw =
			(data as { detail?: unknown }).detail ??
			(data as { error?: unknown }).error ??
			res.statusText;
		let msg: string;
		if (typeof raw === "string") {
			msg = raw;
		} else if (Array.isArray(raw)) {
			msg = raw
				.map((item) => {
					if (item && typeof item === "object") {
						const r = item as { msg?: string; loc?: unknown };
						return r.msg ? String(r.msg) : JSON.stringify(item);
					}
					return String(item);
				})
				.join("; ");
		} else if (raw && typeof raw === "object") {
			const r = raw as { msg?: string; message?: string };
			msg = r.message ? String(r.message) : r.msg ? String(r.msg) : JSON.stringify(raw);
		} else {
			msg = String(raw);
		}
		// The HTTP status travels with the message, so a caller can tell "refused" (413, 422) from "try again" (5xx).
		throw Object.assign(new Error(msg), { status: res.status });
	}
	return data as T;
}

export async function apiUpload<T = unknown>(
	path: string,
	formData: FormData
): Promise<T> {
	const h = await authHeader();
	const res = await fetch(absUrl(path), {
		method: "POST",
		credentials: "include",
		headers: h,
		body: formData,
	});
	if (!res.ok) {
		const text = await res.text();
		throw new Error(text || res.statusText);
	}
	return res.json();
}
